import {
  WebSocketsAuthEvents,
  WebSocketsDataExportEvents,
  WebSocketsDataUploadEvents,
  WebSocketsDriverLocationEvents,
  WebSocketsElectricVehicleEvents,
  WebSocketsEventData,
  WebSocketsIntegrationsEvents,
  WebSocketsModules,
  WebSocketsModulesEvents,
  WebSocketsProductionEvents,
  WebSocketsQueueEvents,
  WebSocketsReservationEvents,
  WebSocketsTripEvents,
} from 'models/web-sockets';
import { ManagerOptions, Socket, SocketOptions, io } from 'socket.io-client';

export const webSocketsModuleEvents: { [Module in WebSocketsModules]: WebSocketsModulesEvents[Module][] } = {
  [WebSocketsModules.Trips]: Object.values(WebSocketsTripEvents),
  [WebSocketsModules.Reservations]: Object.values(WebSocketsReservationEvents),
  [WebSocketsModules.DriverLocations]: Object.values(WebSocketsDriverLocationEvents),
  [WebSocketsModules.DataUpload]: Object.values(WebSocketsDataUploadEvents),
  [WebSocketsModules.Auth]: Object.values(WebSocketsAuthEvents),
  [WebSocketsModules.DataExport]: Object.values(WebSocketsDataExportEvents),
  [WebSocketsModules.Production]: Object.values(WebSocketsProductionEvents),
  [WebSocketsModules.ElectricVehicle]: Object.values(WebSocketsElectricVehicleEvents),
  [WebSocketsModules.Integrations]: Object.values(WebSocketsIntegrationsEvents),
  [WebSocketsModules.Queue]: Object.values(WebSocketsQueueEvents),
};

/**
 * WebSocket instance and its associated listeners are stored statically to support transitions between WebSocket connections.
 * For example when the session is refreshed and a new access token is provided - a new Web Socket connection is created.
 * See `migrateListeners` method which subscribes all registered listeners to the current connection.
 */
export class SocketIO {
  private static socket: Socket | null = null;
  private static listenersMap: {
    [event: string]: ((data: WebSocketsEventData<WebSocketsModules, WebSocketsModulesEvents[WebSocketsModules]>) => void)[];
  } = {};

  constructor(accessToken: string) {
    console.log(`${SocketIO.socket ? 'Re-' : ''}Connecting to Socket...', ${import.meta.env.VITE_SOCKET_IO_SERVER}`);

    const options: Partial<ManagerOptions & SocketOptions> = {
      query: { accessToken, userType: 'moderator' },
      transports: ['websocket'],
      withCredentials: true, // Support sticky-sessions: https://socket.io/docs/v3/using-multiple-nodes
    };

    SocketIO.socket = io(import.meta.env.VITE_SOCKET_IO_SERVER, options);
    SocketIO.migrateListeners();
    SocketIO.socket.on('connect_error', (err) => console.error(`connect_error due to ${err.message}`));
  }

  public getSocket(): Socket {
    return SocketIO.socket!;
  }

  public onConnectError(callback: (error: Error) => void): void {
    SocketIO.socket!.on('connect_error', callback);
  }

  public onDisconnect(callback: (reason: string, details: unknown) => void): void {
    SocketIO.socket!.on('disconnect', callback);
  }

  public onConnect(callback: (socket: SocketIO) => void): void {
    SocketIO.socket!.on('connect', () => callback(this));
  }

  public on<Module extends WebSocketsModules, Event extends WebSocketsModulesEvents[Module]>(
    model: Module,
    event: Event,
    callback: (data: WebSocketsEventData<Module, Event>) => void,
  ): void {
    const eventName = `${model}:${event}`;

    // Attach the listener to the current socket
    SocketIO.socket!.on(eventName, callback);

    // Store the listener statically to support web socket transitioning
    SocketIO.listenersMap[eventName] ??= [];
    SocketIO.listenersMap[eventName].push(callback);
  }

  public off<Module extends WebSocketsModules, Event extends WebSocketsModulesEvents[Module]>(
    model: Module,
    event: Event,
    callback: (data: WebSocketsEventData<Module, Event>) => void,
  ): void {
    const eventName = `${model}:${event}`;

    SocketIO.socket!.removeListener(eventName, callback);

    // Update the static listeners array accordingly
    if (SocketIO.listenersMap[eventName]) {
      SocketIO.listenersMap[eventName] = SocketIO.listenersMap[eventName].filter((cb) => cb !== callback);

      if (SocketIO.listenersMap[eventName].length === 0) {
        delete SocketIO.listenersMap[eventName];
      }
    }
  }

  /** If we have any registered listeners, re-subscribe them to currently active socket */
  private static migrateListeners<Module extends WebSocketsModules, Event extends WebSocketsModulesEvents[Module]>() {
    if (!SocketIO.socket) {
      console.error('Aborting websocket listeners resubscription because socket instance is undefined');

      return;
    }

    Object.keys(SocketIO.listenersMap).forEach((eventName: string) => {
      SocketIO.listenersMap[eventName].forEach((listener) =>
        SocketIO.socket!.on(eventName, listener as (data: WebSocketsEventData<Module, Event>) => void),
      );
    });
  }

  /** Use when user is not authenticated to ensure registered listeners are cleaned up */
  public static clearListeners = (): void => {
    SocketIO.listenersMap = {};
  };
}
